import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ExternalLink, Trash2 } from 'lucide-react';
import { listTerminals, createTerminal, deleteTerminal } from '../api/terminals';
import { PageLoader } from '../components/PageLoader';
import { StatusBadge } from '../components/StatusBadge';

export function Dashboard() {
  const navigate = useNavigate();
  const [terminals, setTerminals] = useState(null);
  const [limit, setLimit] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const data = await listTerminals();
      setTerminals(data.terminals);
      setLimit(data.limit);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function handleCreate(e) {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      const terminal = await createTerminal({ name: newName.trim() });
      setNewName('');
      setCreating(false);
      navigate(`/terminals/${terminal.id}`);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }

  async function handleDelete(id, e) {
    e.stopPropagation();
    if (!window.confirm('Delete this terminal? This cannot be undone.')) return;
    await deleteTerminal(id);
    refresh();
  }

  if (terminals === null && !error) return <PageLoader label="Loading terminals..." />;

  const remaining = limit != null ? Math.max(0, limit - terminals.length) : null;

  return (
    <div className="dashboard">
      <p className="dashboard-intro">
        WATT lets you spin up a protected REST endpoint backed by your own Node.js.
        {remaining !== null && (
          <>
            {' '}
            This demo account can create up to {limit} terminals - you have{' '}
            <strong>{remaining}</strong> remaining.
          </>
        )}
      </p>

      <div className="dashboard-header">
        <h1>Terminals</h1>
        <button className="btn btn-primary" onClick={() => setCreating(true)}>
          <Plus size={16} />
          New terminal
        </button>
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      {creating && (
        <form className="create-form" onSubmit={handleCreate}>
          <input
            autoFocus
            placeholder="Terminal name, e.g. order-webhook-test"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <button type="submit" className="btn btn-primary">
            Create
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setCreating(false)}>
            Cancel
          </button>
        </form>
      )}

      {terminals && terminals.length === 0 && !creating && (
        <div className="empty-state">
          No terminals yet. Create one to get a REST endpoint you can call in minutes.
        </div>
      )}

      <div className="terminal-list">
        {terminals?.map((t) => (
          <div key={t.id} className="terminal-row" onClick={() => navigate(`/terminals/${t.id}`)}>
            <div className="terminal-row-main">
              <div className="terminal-row-name">{t.name}</div>
              <div className="terminal-row-url">/t/{t.slug}</div>
            </div>
            <div className="terminal-row-meta">
              <StatusBadge status={t.status} />
              <span className="muted">{t.invocationCount} calls</span>
              {t.status === 'deployed' && (
                <a href={t.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                  <ExternalLink size={14} />
                </a>
              )}
              <button className="icon-btn" onClick={(e) => handleDelete(t.id, e)} title="Delete">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
